import { Controller } from "@hotwired/stimulus";

// Treasure Tables from Old Dragon LB1
// Source: db/seeds/chapters/lb1/09-mestrando.md (Table 9.5, 9.6, 9.7, 9.8)
// Source: db/seeds/chapters/lb1/10-itens-magicos.md (Table A1.1)

export default class extends Controller {
	static values = { labels: Object };

	// The sheet's dialog (rendered in place by MonsterContentComponent) and the parts this controller fills.
	static targets = ["dialog", "body", "footer"];

	currentCode = null;
	currentIsLair = false;

	// A history visit skips turbo:before-visit, so the dialog is closed before Turbo caches the page: restored open, it
	// would have lost the selection that filled it. Only this dialog: other modals must survive a same-URL cache.
	connect() {
		document.addEventListener("turbo:before-cache", this.closeBeforeCache);
	}

	disconnect() {
		document.removeEventListener("turbo:before-cache", this.closeBeforeCache);
	}

	closeBeforeCache = () => {
		if (this.dialogTarget.open) this.dialogTarget.close();
	};

	// Table 9.5: Tesouros
	// Format: { chance: "1-2 em 1d6" or null (100%), roll: "2d6x1000" or count, type: "Qualquer"|"Arma"|etc }
	// For lair (A-O): po, pp, pc, gems, objects, magic
	// For individual (P-V): po, pp, pc, equipment, objects, magic
	static TREASURE_TABLES = {
		// Lair Treasures (A-O)
		A: {
			quick: "12.000 PO",
			po: { chance: "1-2", roll: "2d6x1000" },
			pp: { chance: "1-2", roll: "1d6x1000" },
			pc: { chance: "1", roll: "1d6x1000" },
			gems: { chance: "1-3", roll: "6d6" },
			objects: { chance: "1-3", roll: "6d6" },
			magic: { chance: "1-2", roll: 3, type: "Qualquer" },
		},
		B: {
			quick: "1.400 PO",
			po: { chance: "1", roll: "1d3x1000" },
			pp: { chance: "1", roll: "1d6x1000" },
			pc: { chance: "1-3", roll: "1d8x1000" },
			gems: { chance: "1", roll: "1d6" },
			objects: { chance: "1", roll: "1d6" },
			magic: { chance: "1", roll: 1, type: "Arma" },
		},
		C: {
			quick: "650 PO",
			po: null,
			pp: { chance: "1-2", roll: "1d4x1000" },
			pc: { chance: "1-2", roll: "1d12x1000" },
			gems: { chance: "1", roll: "1d4" },
			objects: { chance: "1", roll: "1d4" },
			magic: { chance: "1", roll: 2, type: "Qualquer" },
		},
		D: {
			quick: "3.400 PO",
			po: { chance: "1-3", roll: "1d6x1000" },
			pp: { chance: "1", roll: "1d12x1000" },
			pc: { chance: "1", roll: "1d8x1000" },
			gems: { chance: "1-2", roll: "1d8" },
			objects: { chance: "1-2", roll: "1d8" },
			magic: { chance: "1", roll: 2, type: "Qualquer", extra: [{ roll: 1, type: "Poção" }] },
		},
		E: {
			quick: "1.800 PO",
			po: { chance: "1", roll: "1d8x1000" },
			pp: { chance: "1-2", roll: "1d12x1000" },
			pc: { chance: "1", roll: "1d10x1000" },
			gems: { chance: "1", roll: "1d10" },
			objects: { chance: "1", roll: "1d10" },
			magic: { chance: "1", roll: 3, type: "Qualquer", extra: [{ roll: 1, type: "Pergaminho" }] },
		},
		F: {
			quick: "4.000 PO",
			po: { chance: "1-2", roll: "1d12x1000" },
			pp: { chance: "1", roll: "2d10x1000" },
			pc: null,
			gems: { chance: "1", roll: "2d12" },
			objects: { chance: "1", roll: "1d12" },
			magic: {
				chance: "1-2",
				roll: 3,
				type: "Não Arma",
				extra: [
					{ roll: 1, type: "Poção" },
					{ roll: 1, type: "Pergaminho" },
				],
			},
		},
		G: {
			quick: "14.000 PO",
			po: { chance: "1-3", roll: "10d4x1000" },
			pp: null,
			pc: null,
			gems: { chance: "1", roll: "3d6" },
			objects: { chance: "1", roll: "1d10" },
			magic: { chance: "1-2", roll: 4, type: "Qualquer", extra: [{ roll: 1, type: "Pergaminho" }] },
		},
		H: {
			quick: "27.000 PO",
			po: { chance: "1-3", roll: "10d6x1000" },
			pp: { chance: "1-3", roll: "1d10x1000" },
			pc: { chance: "1", roll: "3d8x1000" },
			gems: { chance: "1-3", roll: "1d10" },
			objects: { chance: "1-3", roll: "10d4" },
			magic: {
				chance: "1",
				roll: 4,
				type: "Qualquer",
				extra: [
					{ roll: 1, type: "Poção" },
					{ roll: 1, type: "Pergaminho" },
				],
			},
		},
		I: {
			quick: "2.800 PO",
			po: null,
			pp: null,
			pc: null,
			gems: { chance: "1-3", roll: "2d6" },
			objects: { chance: "1-3", roll: "2d6" },
			magic: { chance: "1", roll: 1, type: "Qualquer" },
		},
		J: {
			quick: "25 PO",
			po: null,
			pp: { chance: "1", roll: "1d3x1000" },
			pc: { chance: "1", roll: "1d4x1000" },
			gems: null,
			objects: null,
			magic: null,
		},
		K: {
			quick: "15 PO",
			po: null,
			pp: { chance: "1", roll: "1d2x1000" },
			pc: null,
			gems: null,
			objects: null,
			magic: null,
		},
		L: {
			quick: "200 PO",
			po: null,
			pp: null,
			pc: null,
			gems: { chance: "1-3", roll: "1d4" },
			objects: null,
			magic: null,
		},
		M: {
			quick: "40.000 PO",
			po: { chance: "1-5", roll: "8d10x1000" },
			pp: { chance: "1-3", roll: "10d6x1000" },
			pc: null,
			gems: { chance: "1-3", roll: "5d4" },
			objects: { chance: "1-2", roll: "2d6" },
			magic: null,
		},
		N: {
			quick: "-",
			po: null,
			pp: null,
			pc: null,
			gems: null,
			objects: null,
			magic: { chance: "1-2", roll: "2d4", type: "Poção" },
		},
		O: {
			quick: "-",
			po: null,
			pp: null,
			pc: null,
			gems: null,
			objects: null,
			magic: { chance: "1-3", roll: "1d4", type: "Poção" },
		},
		// Individual Treasures (P-V) - uses "equipment" instead of "gems"
		P: {
			quick: "1 PP",
			po: null,
			pp: null,
			pc: { chance: null, roll: "3d8" },
			equipment: null,
			objects: null,
			magic: null,
		},
		Q: {
			quick: "1 PO",
			po: null,
			pp: { chance: null, roll: "3d6" },
			pc: null,
			equipment: null,
			objects: null,
			magic: null,
		},
		R: {
			quick: "3 PO",
			po: { chance: null, roll: "1d6" },
			pp: null,
			pc: null,
			equipment: { chance: "1-2", roll: 1 },
			objects: null,
			magic: null,
		},
		S: {
			quick: "5 PO",
			po: { chance: null, roll: "2d4" },
			pp: null,
			pc: null,
			equipment: { chance: "1-2", roll: 1 },
			objects: null,
			magic: null,
		},
		T: {
			quick: "17 PO",
			po: { chance: null, roll: "1d6x5" },
			pp: null,
			pc: null,
			equipment: { chance: "1-2", roll: 2 },
			objects: null,
			magic: null,
		},
		U: {
			quick: "90 PO",
			po: { chance: "1", roll: "1d10" },
			pp: { chance: "1", roll: "1d10" },
			pc: { chance: "1", roll: "1d10" },
			equipment: { chance: "1", roll: "1d4" },
			objects: { chance: "1", roll: 1 },
			magic: { chance: "1", roll: 1, type: "Qualquer" },
		},
		V: {
			quick: "175 PO",
			po: { chance: "1-2", roll: "1d10" },
			pp: { chance: "1-2", roll: "1d10" },
			pc: null,
			equipment: { chance: "1", roll: "1d6" },
			objects: { chance: "1", roll: "1d4" },
			magic: { chance: "1-2", roll: 1, type: "Qualquer" },
		},
	};

	// Table 9.6: Equipamentos em Tesouros
	static EQUIPMENT_TABLE = {
		2: { type: "Raro", comum: "Símbolo Divino", incomum: "Aljava (1d6 flechas)", raro: "Porta Mapas" },
		3: { type: "Raro", comum: "Saco de Dormir", incomum: "Martelo", raro: "Pena e Tinta" },
		4: { type: "Incomum", comum: "Ração de Viagem (1d4)", incomum: "Óleo", raro: "Corrente" },
		5: { type: "Incomum", comum: "Pederneira", incomum: "Água Benta", raro: "Algema" },
		6: { type: "Comum", comum: "Corda de Cânhamo (15m)", incomum: "Pá ou Picareta", raro: "Giz" },
		7: { type: "Comum", comum: "Tochas (1d4)", incomum: "Arpéu", raro: "Caixa Pequena" },
		8: { type: "Comum", comum: "Mochila", incomum: "Lamparina", raro: "Coberta de Inverno" },
		9: { type: "Comum", comum: "Odre", incomum: "Vela (1d4)", raro: "Espelho" },
		10: { type: "Incomum", comum: "Saco de Estopa", incomum: "Cravos ou Ganchos (1d4)", raro: "Cadeado" },
		11: { type: "Incomum", comum: "Traje de Exploração", incomum: "Traje de Inverno", raro: "Traje Nobre" },
		12: { type: "Raro", comum: "Ferramenta de Ladrão", incomum: "Lanterna Furta-Fogo", raro: "Rede" },
	};

	// Table 9.7: Objetos de Valor
	static OBJECTS_TABLE = {
		types: {
			2: "Obras de Arte",
			3: "Obras de Arte",
			4: "Utensílios",
			5: "Utensílios",
			6: "Mercadoria",
			7: "Mercadoria",
			8: "Mercadoria",
			9: "Mercadoria",
			10: "Louças",
			11: "Louças",
			12: "Joias",
		},
		items: {
			Mercadoria: {
				2: "Peles de Animais Raros*",
				3: "Peles de Animais Raros*",
				4: "Objetos de Marfim",
				5: "Objetos de Marfim",
				6: "Sacas de Especiaria*",
				7: "Sacas de Especiaria*",
				8: "Sacas de Incenso*",
				9: "Sacas de Incenso*",
				10: "Tecidos Nobres*",
				11: "Tecidos Nobres*",
				12: "Metros de Fina Seda*",
			},
			Louças: {
				2: "Objetos de Vidro Soprado",
				3: "Objetos de Vidro Soprado",
				4: "Copos de Vidro e com Prata",
				5: "Copos de Vidro e com Prata",
				6: "Baixelas de Louça",
				7: "Baixelas de Louça",
				8: "Baixelas de Porcelana com ouro",
				9: "Baixelas de Porcelana com ouro",
				10: "Vaso de Porcelana",
				11: "Vaso de Porcelana",
				12: "Cálices de Vidro com pedraria",
			},
			Utensílios: {
				2: "Religiosos de Cobre",
				3: "Religiosos de Cobre",
				4: "Talheres de Prata",
				5: "Talheres de Prata",
				6: "Candelabros de Prata",
				7: "Candelabros de Prata",
				8: "Cutelaria Fina",
				9: "Cutelaria Fina",
				10: "Cálices de Ouro",
				11: "Cálices de Ouro",
				12: "Religiosos de Ouro",
			},
			"Obras de Arte": {
				2: "Móveis com Marchetaria*",
				3: "Móveis com Marchetaria*",
				4: "Tapeçaria Fina*",
				5: "Tapeçaria Fina*",
				6: "Livro Raro",
				7: "Livro Raro",
				8: "Escultura*",
				9: "Escultura*",
				10: "Tela Pintada*",
				11: "Tela Pintada*",
				12: "Estatueta em Bronze*",
			},
			Joias: {
				2: "Cordão de Prata",
				3: "Cordão de Prata",
				4: "Brincos de Pérola",
				5: "Brincos de Pérola",
				6: "Bracelete de Prata",
				7: "Bracelete de Prata",
				8: "Pingente de Pedraria",
				9: "Pingente de Pedraria",
				10: "Camafeu de Ouro",
				11: "Camafeu de Ouro",
				12: "Tiara com Pedraria",
			},
		},
	};

	// Table 9.8: Gemas
	static GEMS_TABLE = {
		2: { category: "Preciosa", value: 500 },
		3: { category: "Preciosa", value: 500 },
		4: { category: "Ornamental", value: 50 },
		5: { category: "Ornamental", value: 50 },
		6: { category: "Decorativa", value: 10 },
		7: { category: "Decorativa", value: 10 },
		8: { category: "Decorativa", value: 10 },
		9: { category: "Decorativa", value: 10 },
		10: { category: "Semipreciosa", value: 100 },
		11: { category: "Semipreciosa", value: 100 },
		12: { category: "Joia", value: 1000 },
	};

	// Table A1.1: Itens Mágicos (simplified - d100 ranges)
	static MAGIC_ITEMS_TABLE = {
		Todos: [
			{ range: [1, 3], item: "Espada Longa -1 Amaldiçoada (Caótica)" },
			{ range: [4, 7], item: "Espada Longa +1" },
			{ range: [8, 11], item: "Espada Longa +1/+2 contra licantropos" },
			{ range: [12, 15], item: "Espada Longa +1/+2 contra orcs" },
			{ range: [16, 19], item: "Espada Longa +1/+2 contra mortos-vivos" },
			{ range: [20, 20], item: "Espada Longa +2" },
			{ range: [21, 22], item: "Arma -1 Amaldiçoada (Caótica)" },
			{ range: [23, 23], item: "Flechas +1 (10 unidades)" },
			{ range: [24, 25], item: "Machado de Batalha +1" },
			{ range: [26, 27], item: "Martelo de Batalha +1" },
			{ range: [28, 29], item: "Adaga +1" },
			{ range: [30, 30], item: "Adaga +2" },
			{ range: [31, 32], item: "Armadura -1 Amaldiçoada (Caótica)" },
			{ range: [33, 34], item: "Armadura Acolchoada +1" },
			{ range: [35, 36], item: "Armadura de Couro +1" },
			{ range: [37, 37], item: "Armadura de Couro Batido +1" },
			{ range: [38, 38], item: "Cota de Malha +1" },
			{ range: [39, 40], item: "Escudo +1" },
			{ range: [41, 44], item: "Poção Amaldiçoada (Caótica)" },
			{ range: [45, 60], item: "Poção de Cura" },
			{ range: [61, 61], item: "Poção da Diminuição" },
			{ range: [62, 62], item: "Poção da Forma Gasosa" },
			{ range: [63, 63], item: "Poção da Força Gigante" },
			{ range: [64, 65], item: "Venenos" },
			{ range: [66, 67], item: "Pergaminho Amaldiçoado (Caótico)" },
			{ range: [68, 77], item: "Pergaminho Arcano" },
			{ range: [78, 81], item: "Pergaminho Divino" },
			{ range: [82, 83], item: "Pergaminho de Proteção" },
			{ range: [84, 84], item: "Mapa de Tesouro" },
			{ range: [85, 85], item: "Anel (Caótico)" },
			{ range: [86, 86], item: "Anel de Proteção +1" },
			{ range: [87, 87], item: "Anel do Controle de Animais" },
			{ range: [88, 88], item: "Anel da Regeneração" },
			{ range: [89, 89], item: "Anel da Invisibilidade" },
			{ range: [90, 90], item: "Haste Caótica (Caótico)" },
			{ range: [91, 91], item: "Varinha de Paralisação" },
			{ range: [92, 92], item: "Varinha de Bolas de Fogo" },
			{ range: [93, 93], item: "Cajado da Cura" },
			{ range: [94, 94], item: "Cajado de Ataque" },
			{ range: [95, 95], item: "Bastão do Cancelamento" },
			{ range: [96, 96], item: "Sacola Devoradora (Caótico)" },
			{ range: [97, 97], item: "Bola de Cristal" },
			{ range: [98, 98], item: "Manto Élfico" },
			{ range: [99, 99], item: "Botas Élficas" },
			{ range: [100, 100], item: "Manoplas da Força do Ogro" },
		],
		// Source table has overlap at 4-6/5-6, adjusted to sequential ranges
		"Não Arma": [
			{ range: [1, 3], item: "Armadura -1 Amaldiçoada (Caótica)" },
			{ range: [4, 5], item: "Armadura Acolchoada +1" },
			{ range: [6, 6], item: "Armadura de Couro +1" },
			{ range: [7, 8], item: "Armadura de Couro Batido +1" },
			{ range: [9, 10], item: "Cota de Malha +1" },
			{ range: [11, 14], item: "Escudo +1" },
			{ range: [15, 20], item: "Poção Amaldiçoada (Caótica)" },
			{ range: [21, 30], item: "Poção de Cura" },
			{ range: [31, 35], item: "Poção da Diminuição" },
			{ range: [36, 40], item: "Poção da Forma Gasosa" },
			{ range: [41, 45], item: "Poção da Força Gigante" },
			{ range: [46, 50], item: "Venenos" },
			{ range: [51, 54], item: "Pergaminho Amaldiçoado (Caótico)" },
			{ range: [55, 73], item: "Pergaminho Arcano" },
			{ range: [74, 79], item: "Pergaminho Divino" },
			{ range: [80, 81], item: "Pergaminho de Proteção" },
			{ range: [82, 84], item: "Mapa de Tesouro" },
			{ range: [85, 85], item: "Anel (Caótico)" },
			{ range: [86, 86], item: "Anel de Proteção +1" },
			{ range: [87, 87], item: "Anel do Controle de Animais" },
			{ range: [88, 88], item: "Anel da Regeneração" },
			{ range: [89, 89], item: "Anel da Invisibilidade" },
			{ range: [90, 90], item: "Haste Caótica (Caótico)" },
			{ range: [91, 91], item: "Varinha de Paralisação" },
			{ range: [92, 92], item: "Varinha de Bolas de Fogo" },
			{ range: [93, 93], item: "Cajado da Cura" },
			{ range: [94, 94], item: "Cajado de Ataque" },
			{ range: [95, 95], item: "Bastão do Cancelamento" },
			{ range: [96, 96], item: "Sacola Devoradora (Caótico)" },
			{ range: [97, 97], item: "Bola de Cristal" },
			{ range: [98, 98], item: "Manto Élfico" },
			{ range: [99, 99], item: "Botas Élficas" },
			{ range: [100, 100], item: "Manoplas da Força do Ogro" },
		],
		Arma: [
			{ range: [1, 10], item: "Espada Longa -1 Amaldiçoada (Caótica)" },
			{ range: [11, 30], item: "Espada Longa +1" },
			{ range: [31, 40], item: "Espada Longa +1/+2 contra licantropos" },
			{ range: [41, 50], item: "Espada Longa +1/+2 contra orcs" },
			{ range: [51, 60], item: "Espada Longa +1/+2 contra mortos-vivos" },
			{ range: [61, 65], item: "Espada Longa +2" },
			{ range: [66, 75], item: "Arma -1 Amaldiçoada (Caótica)" },
			{ range: [76, 81], item: "Flechas +1 (10 unidades)" },
			{ range: [82, 87], item: "Machado de Batalha +1" },
			{ range: [88, 93], item: "Martelo de Batalha +1" },
			{ range: [94, 98], item: "Adaga +1" },
			{ range: [99, 100], item: "Adaga +2" },
		],
		Poção: [
			{ range: [1, 16], item: "Poção Amaldiçoada (Caótica)" },
			{ range: [17, 85], item: "Poção de Cura" },
			{ range: [86, 88], item: "Poção da Diminuição" },
			{ range: [89, 91], item: "Poção da Forma Gasosa" },
			{ range: [92, 94], item: "Poção da Força Gigante" },
			{ range: [95, 100], item: "Venenos" },
		],
		Pergaminho: [
			{ range: [1, 10], item: "Pergaminho Amaldiçoado (Caótico)" },
			{ range: [11, 65], item: "Pergaminho Arcano" },
			{ range: [66, 85], item: "Pergaminho Divino" },
			{ range: [86, 95], item: "Pergaminho de Proteção" },
			{ range: [96, 100], item: "Mapa de Tesouro" },
		],
	};

	// The buttons name the table through action params; the dialog's open/close/Esc/backdrop belong to the modal controller.
	openModal(event) {
		event.preventDefault();

		this.currentCode = event.params.code;
		this.currentIsLair = event.params.lair;

		this.bodyTarget.innerHTML = this.buildTableInfoHTML();
		this.footerTarget.classList.toggle("hidden", this.isEspecial());
		this.dialogTarget.showModal();
	}

	isEspecial() {
		return this.currentCode.toLowerCase() === "especial";
	}

	rollTreasure() {
		if (!this.currentCode || this.isEspecial()) return;

		const codes = this.parseCodes(this.currentCode);

		let html = this.buildTableInfoHTML();
		html += '<div class="mt-4 p-3 bg-cinza-50 dark:bg-surface-sunken rounded border border-cinza-200 dark:border-line">';
		html += `<h3 class="font-serif text-lg font-bold text-carmesim-700 dark:text-link">${this.labelsValue.generated}</h3>`;

		for (const { code, multiplier, divider } of codes) {
			const table = this.constructor.TREASURE_TABLES[code.toUpperCase()];
			if (!table) continue;

			for (let i = 0; i < multiplier; i++) {
				if (multiplier > 1) {
					html += `<p class="my-2 text-sm text-cinza-600 dark:text-ink-muted"><strong>${this.fill(this.labelsValue.type, { code: code.toUpperCase() })}</strong> ${this.fill(this.labelsValue.roll_n, { n: i + 1 })}</p>`;
				}
				const generated = this.generateTreasure(table, code.toUpperCase(), divider);
				html += this.formatTreasureHTML(generated);
			}
		}

		html += "</div>";
		this.bodyTarget.innerHTML = html;
	}

	buildTableInfoHTML() {
		if (this.isEspecial()) {
			return `<div class="p-3 bg-cinza-50 dark:bg-surface-sunken rounded border border-cinza-200 dark:border-line">
				<p class="text-lg font-bold text-carmesim-700 dark:text-link">${this.labelsValue.special}</p>
				<p class="mt-2"><em>${this.labelsValue.special_hint}</em></p>
			</div>`;
		}

		const codes = this.parseCodes(this.currentCode);
		const categoryLabel = this.currentIsLair ? this.labelsValue.lair : this.labelsValue.carried;

		let html = "";

		for (const { code, multiplier, divider } of codes) {
			const table = this.constructor.TREASURE_TABLES[code.toUpperCase()];
			if (!table) {
				html += `<p class="text-carmesim-600 dark:text-link">${this.fill(this.labelsValue.not_found, { code })}</p>`;
				continue;
			}

			const modifierStr = multiplier > 1 ? ` ×${multiplier}` : divider > 1 ? ` ÷${divider}` : "";

			html += `<div class="p-3 bg-cinza-50 dark:bg-surface-sunken rounded border border-cinza-200 dark:border-line">`;
			html += `<h3 class="font-serif text-lg font-bold text-carmesim-700 dark:text-link">${categoryLabel}: ${this.fill(this.labelsValue.type, { code: code.toUpperCase() })} ${modifierStr}</h3>`;
			html += `<p class="my-2"><strong>${this.labelsValue.quick}</strong>: ${table.quick}${modifierStr}</p>`;

			html += '<table class="w-full text-sm">';
			html += "<tbody>";

			if (table.po) html += this.buildTableRow(this.labelsValue.gold_row, table.po);
			if (table.pp) html += this.buildTableRow(this.labelsValue.silver_row, table.pp);
			if (table.pc) html += this.buildTableRow(this.labelsValue.copper_row, table.pc);
			if (table.gems) html += this.buildTableRow(this.labelsValue.gems, table.gems);
			if (table.equipment) html += this.buildTableRow(this.labelsValue.equipment, table.equipment);
			if (table.objects) html += this.buildTableRow(this.labelsValue.objects, table.objects);
			if (table.magic) html += this.buildMagicRow(table.magic);

			html += "</tbody></table>";
			html += "</div>";
		}

		return html;
	}

	buildTableRow(label, data) {
		const chanceStr = data.chance ? this.fill(this.labelsValue.chance, { chance: data.chance }) : "100%";
		const rollStr = typeof data.roll === "number" ? data.roll : data.roll.replace(/x/g, "×");
		return `<tr class="border-b border-cinza-200 dark:border-line">
			<td class="py-1 font-medium">${label}</td>
			<td class="py-1 text-right text-cinza-600 dark:text-ink-muted">${chanceStr}</td>
			<td class="py-1 text-right font-mono">${rollStr}</td>
		</tr>`;
	}

	buildMagicRow(magic) {
		const chanceStr = magic.chance ? this.fill(this.labelsValue.chance, { chance: magic.chance }) : "100%";
		const rollStr = typeof magic.roll === "number" ? magic.roll : magic.roll.replace(/x/g, "×");
		const typeStr = !magic.type || magic.type === "Qualquer" ? this.labelsValue.any : magic.type;

		let extraStr = "";
		if (magic.extra) {
			const extras = magic.extra.map((e) => `+${e.roll} ${e.type}`).join(", ");
			extraStr = ` <span class="text-cinza-500 dark:text-ink-muted">(${extras})</span>`;
		}

		return `<tr class="border-b border-cinza-200 dark:border-line">
			<td class="py-1 font-medium">${this.labelsValue.magic}</td>
			<td class="py-1 text-right text-cinza-600 dark:text-ink-muted">${chanceStr}</td>
			<td class="py-1 text-right font-mono">${rollStr} ${typeStr}${extraStr}</td>
		</tr>`;
	}

	formatList(label, items) {
		if (!items.length) return "";
		const lis = items.map((item) => `<li>${item}</li>`).join("");
		return `<div><span class="font-medium">${label}:</span><ul class="ml-6 list-disc">${lis}</ul></div>`;
	}

	formatTreasureHTML(treasure) {
		let html = '<div class="mt-2 space-y-2">';

		const number = (value) => value.toLocaleString(document.documentElement.lang || "pt-BR");

		// Coins
		const coins = [];
		if (treasure.po > 0) coins.push(`<span class="font-bold">${number(treasure.po)} ${this.labelsValue.gold}</span>`);
		if (treasure.pp > 0) coins.push(`<span class="font-bold">${number(treasure.pp)} ${this.labelsValue.silver}</span>`);
		if (treasure.pc > 0) coins.push(`<span class="font-bold">${number(treasure.pc)} ${this.labelsValue.copper}</span>`);

		if (coins.length > 0) {
			html += `<p><span class="font-medium">${this.labelsValue.coins}:</span> ${coins.join(", ")}</p>`;
		}

		html += this.formatList(this.labelsValue.gems, treasure.gems);
		html += this.formatList(this.labelsValue.equipment, treasure.equipment);
		html += this.formatList(this.labelsValue.objects, treasure.objects);
		html += this.formatList(this.labelsValue.magic, treasure.magic);

		const hasAny = coins.length > 0 || treasure.gems.length > 0 || treasure.equipment.length > 0 || treasure.objects.length > 0 || treasure.magic.length > 0;

		if (!hasAny) {
			html += `<p><em>${this.labelsValue.none}</em></p>`;
		}

		html += "</div>";
		return html;
	}

	// Parse codes like "S", "B+C", "Sx2", "B+Cx2", "F/2", "F÷2"
	parseCodes(codeStr) {
		const results = [];

		// Check for multiplier (x2, ×2) or divider (/2, ÷2)
		const multiplierMatch = codeStr.match(/[×x](\d+)$/i);
		const dividerMatch = codeStr.match(/[÷/](\d+)$/i);

		let multiplier = 1;
		let divider = 1;
		let cleanStr = codeStr;

		if (multiplierMatch) {
			multiplier = Number.parseInt(multiplierMatch[1], 10);
			cleanStr = codeStr.replace(/[×x]\d+$/i, "");
		} else if (dividerMatch) {
			divider = Number.parseInt(dividerMatch[1], 10);
			cleanStr = codeStr.replace(/[÷/]\d+$/i, "");
		}

		const codes = cleanStr.split("+");
		for (const code of codes) {
			results.push({ code: code.trim(), multiplier, divider });
		}
		return results;
	}

	// Roll dice: "2d6", "1d6x1000", "1d6x5", etc.
	rollDice(diceStr) {
		if (typeof diceStr === "number") return diceStr;

		const str = String(diceStr).toLowerCase();
		const match = str.match(/^(\d+)d(\d+)(?:x(\d+))?$/);
		if (!match) return 0;

		const count = Number.parseInt(match[1], 10);
		const sides = Number.parseInt(match[2], 10);
		const multiplierValue = match[3] ? Number.parseInt(match[3], 10) : 1;

		let total = 0;
		for (let i = 0; i < count; i++) {
			total += Math.floor(Math.random() * sides) + 1;
		}
		return total * multiplierValue;
	}

	// Check chance: "1-2" means roll 1d6, success if 1 or 2
	checkChance(chanceStr) {
		if (chanceStr === null) return true;

		const match = String(chanceStr).match(/^(\d+)(?:-(\d+))?$/);
		if (!match) return true;

		const min = Number.parseInt(match[1], 10);
		const max = match[2] ? Number.parseInt(match[2], 10) : min;
		const roll = Math.floor(Math.random() * 6) + 1;
		return roll >= min && roll <= max;
	}

	// Roll amount with chance check and divider
	rollAmount(entry, divider) {
		if (!entry || !this.checkChance(entry.chance)) return 0;
		return Math.floor(this.rollDice(entry.roll) / divider);
	}

	// Generate multiple items using a generator function
	generateItems(entry, divider, generator) {
		const count = this.rollAmount(entry, divider);
		return Array.from({ length: count }, generator);
	}

	// Generate treasure from a table row
	generateTreasure(table, _code, divider = 1) {
		const result = {
			po: this.rollAmount(table.po, divider),
			pp: this.rollAmount(table.pp, divider),
			pc: this.rollAmount(table.pc, divider),
			gems: this.generateItems(table.gems, divider, () => this.generateGem()),
			equipment: this.generateItems(table.equipment, divider, () => this.generateEquipment()),
			objects: this.generateItems(table.objects, divider, () => this.generateObject()),
			magic: [],
		};

		// Magic items have special handling for extras
		if (table.magic && this.checkChance(table.magic.chance)) {
			const type = table.magic.type || "Qualquer";
			const count = Math.floor(this.rollDice(table.magic.roll) / divider);
			for (let i = 0; i < count; i++) {
				result.magic.push(this.generateMagicItem(type));
			}
			for (const extra of table.magic.extra || []) {
				const extraCount = Math.floor(this.rollDice(extra.roll) / divider);
				for (let i = 0; i < extraCount; i++) {
					result.magic.push(this.generateMagicItem(extra.type));
				}
			}
		}

		return result;
	}

	generateGem() {
		const roll = this.rollDice("2d6");
		const gem = this.constructor.GEMS_TABLE[roll];
		let value = gem.value;
		const modifiers = [];

		if (this.checkChance("1-2")) {
			value = Math.floor(value * 0.75);
			modifiers.push(this.labelsValue.uncut);
		}

		if (this.checkChance("1")) {
			value = Math.floor(value * 0.5);
			modifiers.push(this.labelsValue.damaged);
		}

		const modStr = modifiers.length > 0 ? ` (${modifiers.join(", ")})` : "";
		return `${gem.category}${modStr}: ${value} ${this.labelsValue.gold}`;
	}

	generateEquipment() {
		const typeRoll = this.rollDice("2d6");
		const typeRow = this.constructor.EQUIPMENT_TABLE[typeRoll];
		const column = typeRow.type.toLowerCase();

		const itemRoll = this.rollDice("2d6");
		const itemRow = this.constructor.EQUIPMENT_TABLE[itemRoll];

		return itemRow[column];
	}

	generateObject() {
		const typeRoll = this.rollDice("2d6");
		const type = this.constructor.OBJECTS_TABLE.types[typeRoll];
		const itemRoll = this.rollDice("2d6");
		const item = this.constructor.OBJECTS_TABLE.items[type][itemRoll];
		const value = this.rollDice("2d6") * 100;

		const isHeavy = item.endsWith("*");
		const cleanItem = isHeavy ? item.slice(0, -1) : item;
		const heavyStr = isHeavy ? ` ${this.labelsValue.heavy}` : "";

		return `${cleanItem}${heavyStr}: ${value} ${this.labelsValue.gold}`;
	}

	generateMagicItem(type) {
		const column = type === "Qualquer" ? "Todos" : type;
		const table = this.constructor.MAGIC_ITEMS_TABLE[column] || this.constructor.MAGIC_ITEMS_TABLE.Todos;

		const roll = Math.floor(Math.random() * 100) + 1;
		for (const entry of table) {
			if (roll >= entry.range[0] && roll <= entry.range[1]) {
				return entry.item;
			}
		}
		return this.labelsValue.magic_item;
	}

	// Replaces the %{name} placeholders of a label, as map_presence_controller does.
	fill(text, values) {
		return text.replace(/%\{(\w+)\}/g, (_, name) => values[name]);
	}
}
